import { z } from 'zod';

/**
 * The api-client types `Document.attachmentStatus` as a plain `string` on
 * purpose, so that a new backend status can never break response parsing. This
 * list is the frontend's single source of truth for the values we recognise.
 */
export const knownAttachmentStatuses = [
  'broken_image',
  'extracted_archive',
  'failed_to_extract',
  'filtered_by_hook_custom',
  'filtered_by_inbox_file_name',
  'filtered_by_inbox_mime_type',
  'filtered_by_inbox_resolution',
  'filtered_by_inbox_size',
  'filtered_by_insecure_mime_type',
  'filtered_by_queue_mime_type',
  'filtered_by_thread_duplicate',
  'hook_additional_file',
  'hook_converted',
  'hook_failed',
  'hook_processed',
  'password_protected_archive',
  'processed',
  'upload_created_task_failed',
  'uploads_failed',
] as const;

export type KnownAttachmentStatus = (typeof knownAttachmentStatuses)[number];

export type AttachmentStatus = KnownAttachmentStatus | (string & {}) | null;

export const attachmentStatusSchema = z
  .enum([...knownAttachmentStatuses, 'unknown'])
  .catch('unknown');

export type ParsedAttachmentStatus = z.infer<typeof attachmentStatusSchema>;

/**
 * Statuses meaning the document never made it through automated processing, so
 * its original file has not been checked for potential risks.
 */
export const failedProcessingAttachmentStatuses = [
  'hook_failed',
  'upload_created_task_failed',
] as const satisfies ReadonlyArray<KnownAttachmentStatus>;

export const hasFailedProcessing = (
  status: AttachmentStatus | undefined
): boolean =>
  failedProcessingAttachmentStatuses.some(
    failedStatus => failedStatus === status
  );
